import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './Icons';
import './LandingPage.css';

export default function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'TrackThrough | From Applied to Hired';
  }, []);

  return (
    <div className="landing">
      {/* ─── Nav ─────────────────────────────────────── */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-logo">
            <div className="logo-mark-sm">T T</div>
            <span>TrackThrough</span>
          </div>
          <div className="landing-nav-right">
            <button className="landing-nav-btn outline" onClick={() => navigate('/login')}>Log In</button>
          </div>
        </div>
      </nav>

      {/* ─── Hero ────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-content">
            <h1 className="hero-tagline">
              The difference between<br />
              <span className="hero-highlight">applied</span> and <span className="hero-highlight">hired.</span>
            </h1>
            <p className="hero-sub">
              Free <mark className="yellow-mark">note-making</mark> for your job applications — track every company, role, and connection in one place.
            </p>
            <div className="hero-ctas">
              <button className="hero-btn primary" onClick={() => navigate('/login?mode=signup')}>
                Get Started — It's Free
              </button>
              <button className="hero-btn outline" onClick={() => navigate('/login')}>
                Log In
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-mockup">
              <div className="mockup-header">
                <div className="mockup-dot" />
                <div className="mockup-dot" />
                <div className="mockup-dot" />
              </div>
              <div className="mockup-body">
                <div className="mockup-stat-row">
                  <div className="mockup-stat"><span className="ms-val">12</span><span className="ms-label">Companies</span></div>
                  <div className="mockup-stat"><span className="ms-val">18</span><span className="ms-label">Roles</span></div>
                  <div className="mockup-stat"><span className="ms-val">24</span><span className="ms-label">Contacts</span></div>
                  <div className="mockup-stat green"><span className="ms-val">9</span><span className="ms-label">Connected</span></div>
                </div>
                <div className="mockup-company">
                  <div className="mc-name">Goldman Sachs</div>
                  <div className="mc-role"><span>Quantitative Analyst</span><span className="mc-badge offer">Offer</span></div>
                </div>
                <div className="mockup-company">
                  <div className="mc-name">Point72</div>
                  <div className="mc-role"><span>Research Associate</span><span className="mc-badge interview">Interviewing</span></div>
                </div>
                <div className="mockup-company">
                  <div className="mc-name">Adobe</div>
                  <div className="mc-role"><span>Data Scientist</span><span className="mc-badge applied">Applied</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Trust Bar ───────────────────────────────── */}
      <section className="trust-bar">
        <p>Built for job seekers. Used by students, professionals, and career changers.</p>
      </section>

      {/* ─── Features ────────────────────────────────── */}
      <section className="features">
        <div className="features-inner">
          <div className="feature-card">
            <div className="feature-icon"><Icons.Building size={24} /></div>
            <h3>Track</h3>
            <p>Companies, roles, statuses — all in one view. Never lose track of where you applied.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><Icons.StickyNote size={24} /></div>
            <h3>Note</h3>
            <p>Private notes and templates for every application. Prep for interviews, log conversations.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><Icons.Share size={24} /></div>
            <h3>Share</h3>
            <p>Custom status page to share with mentors, friends, or career advisors — on your terms.</p>
          </div>
        </div>
      </section>

      {/* ─── How It Works ────────────────────────────── */}
      <section className="how-it-works">
        <h2>How It Works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-num">1</div>
            <h4>Get started instantly</h4>
            <p>One click with Google or use your email.</p>
          </div>
          <div className="step-line" />
          <div className="step">
            <div className="step-num">2</div>
            <h4>Add companies as you apply</h4>
            <p>Track roles, contacts, and status. Add notes for each application.</p>
          </div>
          <div className="step-line" />
          <div className="step">
            <div className="step-num">3</div>
            <h4>Share or export</h4>
            <p>Send your status page to advisors or export a clean Excel report.</p>
          </div>
        </div>
      </section>

      {/* ─── Privacy ─────────────────────────────────── */}
      <section className="privacy-section">
        <h2>Your data, your rules.</h2>
        <div className="privacy-grid">
          <div className="privacy-item visible">
            <span className="privacy-icon">👁</span>
            <span>Stats, companies, roles</span>
            <span className="privacy-tag public-tag">Shareable</span>
          </div>
          <div className="privacy-item private">
            <span className="privacy-icon">🔒</span>
            <span>Contacts</span>
            <span className="privacy-tag private-tag">Always Private</span>
          </div>
          <div className="privacy-item private">
            <span className="privacy-icon">🔒</span>
            <span>Notes</span>
            <span className="privacy-tag private-tag">Always Private</span>
          </div>
          <div className="privacy-item private">
            <span className="privacy-icon">🔒</span>
            <span>Templates</span>
            <span className="privacy-tag private-tag">Always Private</span>
          </div>
        </div>
        <p className="privacy-note">You control exactly who sees what. Create custom networks with granular permissions.</p>
      </section>

      {/* ─── Final CTA ───────────────────────────────── */}
      <section className="final-cta">
        <h2>Start tracking your applications today.</h2>
        <p>Join thousands of job seekers who stay organized and land interviews faster.</p>
        <button className="hero-btn primary" onClick={() => navigate('/login?mode=signup')}>
          Get Started — It's Free
        </button>
      </section>

      {/* ─── Footer ──────────────────────────────────── */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="footer-logo">
            <div className="logo-mark-sm">T T</div>
            <span>TrackThrough</span>
          </div>
          <p>© 2026 TrackThrough. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
